import { ValueOf } from 'types/util';

// shared
export enum SrpFilterName {
  Page = 'Page',
  Limit = 'Limit',
  MakeModel = 'MakeModel',
  Trims = 'Trims',
  Year = 'Year',
  Condition = 'Condition',
  Cylinders = 'Cylinders',
  Radius = 'Radius',
  SortBy = 'SortBy',
  Price = 'Price',
  Doors = 'Doors',
  Advanced = 'Advanced',
  BodyTypes = 'BodyTypes',
  Colors = 'Colors',
  Drivetrain = 'Drivetrain',
  Powertrain = 'Powertrain',
  FuelEfficiency = 'FuelEfficiency',
  FeaturesTechnology = 'FeaturesTechnology',
  Location = 'Location',
  Mileage = 'Mileage',
  Transmission = 'Transmission',
  TruckOptions = 'TruckOptions',
  Category = 'Category',
  SortNewCarsLast = 'SortNewCarsLast',
}

export type SrpFilterRangeValue = {
  min: number;
  max: number;
};

// make+model
export interface MakeModel {
  make?: string;
  model?: string;
}

export type Trims = string[];

export type SrpCategoryValue = string;

// condition
export type SrpConditionFilterValue = Partial<Record<SrpConditionName, boolean>>;

export type SrpConditionName = 'new' | 'used' | 'certified pre-owned';

// cylinders
export type SrpCylindersFilterValue = Partial<Record<SrpCylinderCount, boolean>>;

export type SrpCylinderCount = 2 | 3 | 4 | 5 | 6 | 8 | 10 | 12;

// radius
export type SrpRadiusFilterValue = 10 | 25 | 50 | 100 | 200 | 300 | 500 | number | string;

// price
export type SrpPriceFilterValue = {
  hideNoPriceListings: boolean;
  min: number;
  max: number;
};

// doors
export type SrpDoorsFilterValue = Partial<Record<SrpDoorCount, boolean>>;

export type SrpDoorCount = 2 | 3 | 4;

// advanced
export type SrpAdvancedFilterValue = Partial<Record<SrpAdvancedFilterName, boolean>>;

export type SrpAdvancedFilterName = 'hideOnlineListings' | 'hideListingsNoImages';

// body styles
// export type Srp

export type SrpBodyTypesFilterValue = Partial<Record<SrpBodyTypeName, boolean>>;

export type SrpBodyTypeName =
  | 'convertible'
  | 'coupe'
  | 'crossover'
  | 'hatchback'
  | 'luxury'
  | 'minivan'
  | 'passenger_cargo_van'
  | 'sedan'
  | 'suv'
  | 'truck'
  | 'wagon';

// colors
export type ExteriorColorType =
  | 'white'
  | 'gray'
  | 'black'
  | 'yellow'
  | 'orange'
  | 'brown'
  | 'red'
  | 'green'
  | 'blue'
  | 'purple'
  | 'silver'
  | 'gold';

export type InteriorColorType = 'white' | 'gray' | 'black' | 'brown' | 'red' | 'blue';

export interface SrpExteriorColorFilterValue {
  white: boolean;
  gray: boolean;
  black: boolean;
  yellow: boolean;
  orange: boolean;
  brown: boolean;
  red: boolean;
  green: boolean;
  blue: boolean;
  purple: boolean;
  silver: boolean;
  gold: boolean;
}

export interface SrpInteriorColorFilterValue {
  white: boolean;
  gray: boolean;
  black: boolean;
  brown: boolean;
  red: boolean;
  blue: boolean;
}

export interface SrpColorsFilterValue {
  exterior: SrpExteriorColorFilterValue;
  interior: SrpInteriorColorFilterValue;
}

// drivetrain
export type SrpDrivetrainFilterValue = Partial<Record<SrpDrivetrainName, boolean>>;

export type SrpDrivetrainName = 'frontWheel' | 'rearWheel' | 'allWheel' | 'fourWheel';

// powertrain
export type SrpPowertrainFilterValue = Partial<Record<SrpPowertrainName, boolean>>;

export type SrpPowertrainName =
  | 'electric'
  | 'diesel'
  | 'gasoline'
  | 'hybrid'
  | 'hydrogenFuelCell'
  | 'naturalGas'
  | 'pluginHybrid';

// fuel efficiency
export type SrpFuelEfficiencyValue = 0 | 20 | 30 | 40 | 50;

// features and technology
export type SrpFeaturesTechnologyFilterValue = Partial<Record<SrpFeaturesTechnologyName, boolean>>;

export type SrpFeaturesTechnologyName =
  | 'backupCamera'
  | 'bluetooth'
  | 'entertainmentDVD'
  | 'handicapAccessible'
  | 'iPodAUXInput'
  | 'laneDepartureWarning'
  | 'leather'
  | 'navigationSystem'
  | 'roofRack'
  | 'sunroofMoonroof'
  | '3rdRowSeats'
  | 'towing'
  | 'warranty';

// location
export interface SrpLocationFilterValue {
  city?: string;
  stateCode?: string;
  latitude?: number;
  longitude?: number;
}

// mileage
export type SrpMileageRangeValue = {
  min: number;
  max: number;
};

// transmission
export type SrpTransmissionFilterValue = Partial<Record<SrpTransmissionName, boolean>>;

export type SrpTransmissionName = 'manual' | 'automatic';

// truck options
export type BedType = 'short' | 'regular' | 'chassis' | 'stepSide' | 'long';

export type CabinType = 'extended' | 'regular' | 'crew';

export type RearWheelType = 'single' | 'dual';

export interface SrpCabinTypeFilterValue {
  extended: boolean;
  regular: boolean;
  crew: boolean;
}

export interface SrpRearWheelFilterValue {
  single: boolean;
  dual: boolean;
}

export interface SrpBedTypeFilterValue {
  short: boolean;
  regular: boolean;
  chassis: boolean;
  stepSide: boolean;
  long: boolean;
}

export interface SrpTruckOptionsFilterValue {
  cabinType: SrpCabinTypeFilterValue;
  rearWheel: SrpRearWheelFilterValue;
  bedType: SrpBedTypeFilterValue;
  bedLength: {
    min: number;
    max: number;
  };
}

export type FilterValues =
  | SrpConditionFilterValue
  | SrpDoorsFilterValue
  | MakeModel
  | Trims
  | SrpCategoryValue
  | SrpAdvancedFilterValue
  | SrpCylindersFilterValue
  | SrpTransmissionFilterValue
  | SrpDrivetrainFilterValue
  | SrpPowertrainFilterValue
  | SrpBodyTypesFilterValue
  | SrpLocationFilterValue
  | SrpFeaturesTechnologyFilterValue
  | SrpFilterRangeValue
  | SrpFuelEfficiencyValue
  | SrpMileageRangeValue
  | SrpPriceFilterValue
  | SrpTruckOptionsFilterValue
  | SrpColorsFilterValue
  | SrpRadiusFilterValue;

export interface FilterPill {
  name: string;
  value: string;
  label: string;
  resetState: { [key: string]: FilterValues };
}

export type SliderBlockValue = {
  min: number;
  max: number;
};

export type CheckboxValue = {
  [key: string]: boolean;
};

//
// state
//
export interface SrpFiltersState {
  initialized: boolean;
  values: SrpFilterValues;
  mobileSidebarOpen: boolean;
}

export interface SrpFilterValues {
  [SrpFilterName.Page]: number | undefined;
  [SrpFilterName.Limit]: number;
  [SrpFilterName.MakeModel]: MakeModel;
  [SrpFilterName.Trims]: Trims;
  [SrpFilterName.Year]: SrpFilterRangeValue;
  [SrpFilterName.Condition]: SrpConditionFilterValue;
  [SrpFilterName.Cylinders]: SrpCylindersFilterValue;
  [SrpFilterName.Radius]: SrpRadiusFilterValue;
  [SrpFilterName.SortBy]: string | undefined;
  [SrpFilterName.Price]: SrpPriceFilterValue;
  [SrpFilterName.Doors]: SrpDoorsFilterValue;
  [SrpFilterName.Advanced]: SrpAdvancedFilterValue;
  [SrpFilterName.BodyTypes]: SrpBodyTypesFilterValue;
  [SrpFilterName.Colors]: SrpColorsFilterValue;
  [SrpFilterName.Drivetrain]: SrpDrivetrainFilterValue;
  [SrpFilterName.Powertrain]: SrpPowertrainFilterValue;
  [SrpFilterName.FuelEfficiency]: SrpFuelEfficiencyValue;
  [SrpFilterName.FeaturesTechnology]: SrpFeaturesTechnologyFilterValue;
  [SrpFilterName.Location]: SrpLocationFilterValue;
  [SrpFilterName.Mileage]: SrpMileageRangeValue;
  [SrpFilterName.Transmission]: SrpTransmissionFilterValue;
  [SrpFilterName.TruckOptions]: SrpTruckOptionsFilterValue;
  [SrpFilterName.Category]: SrpCategoryValue;
  [SrpFilterName.SortNewCarsLast]: boolean | undefined;
}

export type SrpFilterValue = ValueOf<SrpFilterValues>;
